import { useCallback, useMemo } from 'react';
import { isFalsy } from '@core/helpers/boolean';
import { useLiveStreamActiveUser } from '@core/livestream/hooks/useLiveStreamActiveUser';
import { PlayerLayout } from '@core/player/types';
import { createBunchOfActivatedVideoElements } from '@core/video/videoElementWrapper/cache';
import { useLayoutControls } from '@embed/components/VideoPlayer/context/LayoutControlsContext';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerLayoutSelector } from '@embed/components/VideoPlayer/store/selectors';
import { useFirstPartyLiveStream } from '@embed/hooks/useFirstPartyLiveStream';
import { useLiveStreamActiveWhitelist } from '@embed/hooks/useLiveStreamActiveWhitelist';
import { useMuting } from './useMuting';
import { useVideoEl } from './useVideoEl';
/**
 * Provides `onClick` callback to handle user activation or redirecting to first party livestream
 * page if applicable. Callback is supposed to be used as onclick handler for VideoControls
 * player UI which gains the ability to activate user and redirect to first party.
 *
 * Note #1: Activation is always followed by going fullscreen.
 * Note #2: `onClick` is not guaranteed to be executed if the resulting action is to open the first party url.
 *
 * @param props
 * @returns
 */
export function useActivateOrRedirectCallback(props) {
    const { onClick } = props || {};
    const options = useMemo(() => ({
        activate: props?.options?.activate !== undefined ? props?.options.activate : true,
        fullscreen: props?.options?.fullscreen !== undefined
            ? props?.options.fullscreen
            : true,
        unmute: props?.options?.unmute !== undefined ? props?.options.unmute : true,
    }), [
        props?.options?.activate,
        props?.options?.fullscreen,
        props?.options?.unmute,
    ]);
    const videoEl = useVideoEl();
    const video = useStore((state) => state.video);
    const params = useStore((state) => state.params);
    const appContext = useStore((state) => state.appContext);
    const layout = useVideoPlayerStore(videoPlayerLayoutSelector);
    const { isUserMuted, unmute } = useMuting();
    const { isActiveWatch, setActiveWatch } = useLiveStreamActiveUser(video);
    const { isFirstPartyLiveStream, openInFirstParty } = useFirstPartyLiveStream({
        appContext,
        params,
        video,
    });
    const isWhitelisted = useLiveStreamActiveWhitelist();
    const { enterFullscreen } = useLayoutControls();
    return useCallback((event) => {
        createBunchOfActivatedVideoElements();
        if (!isFirstPartyLiveStream) {
            event.stopPropagation();
            videoEl.pause();
            openInFirstParty();
        }
        else {
            if (options.activate && !isActiveWatch) {
                setActiveWatch();
            }
            if (layout !== PlayerLayout.FULLSCREEN &&
                options.fullscreen &&
                !isFalsy(params?.player_fullscreen) &&
                !isWhitelisted) {
                enterFullscreen();
            }
            if (options.unmute &&
                isFalsy(params?.muted) &&
                params?.player_unmute &&
                !isUserMuted) {
                unmute();
            }
            onClick?.(event);
            videoEl.tabIndex = 0;
        }
    }, [
        isFirstPartyLiveStream,
        videoEl,
        openInFirstParty,
        options.activate,
        options.fullscreen,
        options.unmute,
        isActiveWatch,
        layout,
        params?.player_fullscreen,
        params?.muted,
        params?.player_unmute,
        isWhitelisted,
        isUserMuted,
        onClick,
        setActiveWatch,
        enterFullscreen,
        unmute,
    ]);
}
